import {createElement, useCallback, useEffect, useMemo, useState} from 'react';

import {useRouter} from 'next/router';
import {useTranslation} from 'react-i18next';

import type {SliderImage} from 'UI/components/modules/ImageSlider/ImageSlider';
import type {StatTile} from 'UI/components/modules/StatTileGrid';
import type {MapMarker} from 'UI/components/modules/WalkMap';
import type {WaypointPreview} from 'UI/components/modules/WaypointPreviewList';

import {buildResponsiveImageData} from 'Shared/buildResponsiveImageData';
import {useAuth} from 'UI/hooks/useAuth';

import {
    ExploreIcon,
    FamilyRestroomIcon,
    HikingIcon,
    LocationOnIcon,
    RouteIcon,
    ScheduleIcon
} from 'Images/icons';
import {CATEGORY_ICON_MAP} from 'UI/utils/categoryIconMap';
import {buildWalkJsonLd, resolveImageUrl} from 'UI/utils/jsonLd';
import type {WalkReviewSummary} from 'UI/utils/jsonLd';

import type {Walk} from 'types/payload-types';

/**
 * A waypoint as leaned down server-side in `getStaticProps` — the raw `images` relation
 * (full Media objects with every size variant) never reaches the client, only the single
 * picked thumbnail URL/alt needed for the round preview image.
 */
interface LeanPreviewWaypoint {
    /** Unique ID. */
    id: string;
    /** Optional alt text for `imageUrl`. */
    imageAlt?: string;
    /** Optional pre-picked thumbnail URL. */
    imageUrl?: string;
    /** Optional short description. */
    shortDescription?: string | null;
    /** Waypoint title. */
    title: string;
}

const CENTS_PER_EURO = 100;
const MINUTES_PER_HOUR = 60;

/** Map terrain enum values to i18n keys. */
const TERRAIN_KEYS: Record<string, string> = {
    asphalt: 'walkDetail.terrainAsphalt',
    cobblestone: 'walkDetail.terrainCobblestone',
    'forest-path': 'walkDetail.terrainForestPath',
    gravel: 'walkDetail.terrainGravel',
    mixed: 'walkDetail.terrainMixed'
};

/** Map suitableFor enum values to i18n keys. */
const SUITABLE_FOR_KEYS: Record<string, string> = {
    children: 'walkDetail.suitableForChildren',
    dogs: 'walkDetail.suitableForDogs',
    families: 'walkDetail.suitableForFamilies',
    seniors: 'walkDetail.suitableForSeniors',
    stroller: 'walkDetail.suitableForStroller',
    wheelchair: 'walkDetail.suitableForWheelchair'
};

/**
 * Converts walk waypoints to map markers for the preview variant.
 *
 * @param walk The walk document.
 * @returns Array of map markers with coordinates and titles.
 */
export const toPreviewMarkers = (walk: Walk): MapMarker[] => {
    if (!walk.waypoints?.docs) return [];

    return walk.waypoints.docs.reduce<MapMarker[]>((acc, wp, index) => {
        const waypoint = typeof wp === 'object' ? wp : null;

        if (waypoint?.location) {
            acc.push({
                coordinates: waypoint.location,
                description: waypoint.shortDescription ?? undefined,
                id: waypoint.id,
                number: index + 1,
                title: waypoint.title,
                walkId: walk.id
            });
        }

        return acc;
    }, []);
};

/**
 * Formats the walk price for display.
 *
 * @param walk   The walk document.
 * @param isFree Whether the walk is free.
 * @returns A formatted price string like `"Free"` or `"4.99 €"`.
 */
export const formatWalkPrice = (walk: Walk, isFree: boolean): string => {
    if (isFree) return '';

    return `${((walk.pricing?.price ?? 0) / CENTS_PER_EURO).toFixed(2)} €`;
};

/**
 * Formats minutes into a human-readable duration string.
 *
 * @param minutes Total minutes.
 * @returns Formatted string like "1h 15m" or "45m".
 */
const formatDuration = (minutes: number): string => {
    const h = Math.floor(minutes / MINUTES_PER_HOUR);
    const m = minutes % MINUTES_PER_HOUR;

    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;

    return `${m}m`;
};

/**
 * Hook für die Walk-Detailseite. Kapselt Auth-State, Bibliothek-Aktionen,
 * und bereitet Datenstrukturen für die Design-Komponenten vor.
 *
 * @param walk    Das Walk-Dokument.
 * @param reviews Die freigegebenen Bewertungen des Walks (für JSON-LD).
 * @returns Walk-State, Handler und vorbereitete Daten für UI-Komponenten.
 */
export const useWalkDetailPage = (walk: Walk, reviews: WalkReviewSummary[]) => {
    const {addToLibrary, isAuthenticated, isInLibrary, isLoading: authLoading} = useAuth();
    const router = useRouter();
    const {t} = useTranslation();
    const [adding, setAdding] = useState(false);
    const [checkoutStatus, setCheckoutStatus] = useState<'cancelled' | 'pending' | 'success' | null>(null);

    useEffect(() => {
        const {checkout} = router.query;

        if (checkout === 'success') {
            void router.replace(`/walks/${walk.slug}`, undefined, {shallow: true});

            /**
             * Fetches the actual purchase status from the API to determine
             * whether the payment is completed or still pending.
             */
            const checkPurchaseStatus = async () => {
                try {
                    const res = await fetch(
                        `/api/purchases/check-status?walkId=${encodeURIComponent(walk.id)}`,
                        {credentials: 'include'}
                    );

                    if (res.ok) {
                        const json = await res.json() as {status: string};

                        if (json.status === 'completed') {
                            window.umami?.track('purchase_completed', {
                                walkId: walk.id,
                                walkSlug: walk.slug
                            });
                        }

                        setCheckoutStatus(json.status === 'completed' ? 'success' : 'pending');
                    } else {
                        setCheckoutStatus('success');
                    }
                } catch {
                    setCheckoutStatus('success');
                }
            };

            void checkPurchaseStatus();
        } else if (checkout === 'cancelled') {
            setCheckoutStatus('cancelled');
            void router.replace(`/walks/${walk.slug}`, undefined, {shallow: true});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.query]);

    const coverImage = useMemo(
        () => (typeof walk.coverImage === 'object' ? walk.coverImage : null),
        [walk.coverImage]
    );

    const guide = useMemo(
        () => (typeof walk.guide === 'object' ? walk.guide : null),
        [walk.guide]
    );

    const city = useMemo(
        () => (typeof walk.city === 'object' ? walk.city : null),
        [walk.city]
    );

    const guideProfileImage = useMemo(
        () => {
            if (guide?.profileImage && typeof guide.profileImage === 'object') {
                return guide.profileImage;
            }

            return null;
        },
        [guide]
    );

    const isFree = walk.pricing?.isFree !== false;
    const inLibrary = isInLibrary(walk.id);

    /** Build the stat tiles array from walk data. */
    const statTiles = useMemo((): StatTile[] => {
        const tiles: StatTile[] = [];

        if (walk.general?.distance) {
            tiles.push({
                icon: RouteIcon({
                    height: 28,
                    width: 28
                }),
                key: 'distance',
                label: t('walkDetail.distance'),
                value: `${walk.general.distance} ${t('common.km')}`
            });
        }

        if (walk.general?.duration) {
            tiles.push({
                icon: ScheduleIcon({
                    height: 28,
                    width: 28
                }),
                key: 'duration',
                label: t('walkDetail.duration'),
                value: formatDuration(walk.general.duration)
            });
        }

        const waypointCount = walk.waypoints?.totalDocs ?? walk.waypoints?.docs?.length ?? 0;

        if (waypointCount > 0) {
            tiles.push({
                icon: LocationOnIcon({
                    height: 28,
                    width: 28
                }),
                key: 'waypoints',
                label: t('walkDetail.waypoints'),
                value: `${waypointCount} Stops`
            });
        }

        if (walk.general?.terrain && walk.general.terrain.length > 0) {
            tiles.push({
                icon: HikingIcon({
                    height: 28,
                    width: 28
                }),
                key: 'terrain',
                label: t('walkDetail.terrain'),
                value: walk.general.terrain.map(v => t(TERRAIN_KEYS[v] ?? v)).join(', ')
            });
        }

        if (walk.targetAudience?.suitableFor && walk.targetAudience.suitableFor.length > 0) {
            tiles.push({
                icon: FamilyRestroomIcon({
                    height: 28,
                    width: 28
                }),
                key: 'suitability',
                label: t('walkDetail.suitableFor'),
                value: walk.targetAudience.suitableFor.map(v => t(SUITABLE_FOR_KEYS[v] ?? v)).join(', ')
            });
        }

        const cats = (walk.categories ?? []).filter(c => typeof c === 'object');

        for (const cat of cats) {
            const CatIcon = cat.predefinedIcon ? CATEGORY_ICON_MAP[cat.predefinedIcon] : null;

            tiles.push({
                icon: CatIcon
                    ? createElement(CatIcon, {
                        height: 28,
                        width: 28
                    })
                    : ExploreIcon({
                        height: 28,
                        width: 28
                    }),
                key: `category-${cat.id}`,
                label: cat.name,
                value: cat.description ?? ''
            });
        }

        return tiles;
    }, [walk, t]);

    /** Build waypoint preview items. */
    const waypointPreviews = useMemo((): WaypointPreview[] => {
        if (!walk.waypoints?.docs) return [];

        return walk.waypoints.docs.reduce<WaypointPreview[]>((acc, wp, index) => {
            const waypoint = typeof wp === 'object' ? (wp as unknown as LeanPreviewWaypoint) : null;

            if (waypoint) {
                acc.push({
                    description: waypoint.shortDescription ?? undefined,
                    href: inLibrary ? `/walks/${walk.slug}/start/${index + 1}` : undefined,
                    id: waypoint.id,
                    imageAlt: waypoint.imageAlt,
                    imageUrl: waypoint.imageUrl,
                    isUnlocked: Boolean(inLibrary),
                    number: index + 1,
                    title: waypoint.title
                });
            }

            return acc;
        }, []);
    }, [walk.waypoints, inLibrary]);

    /** Hero section data. */
    const heroImageUrl = coverImage?.sizes?.hero?.url ?? undefined;
    const durationLabel = walk.general?.duration ? formatDuration(walk.general.duration) : undefined;
    const priceLabel = isFree ? t('common.free') : formatWalkPrice(walk, isFree);

    /** Build slider image array from coverImage + gallery. */
    const heroImages = useMemo((): SliderImage[] => {
        const result: SliderImage[] = [];

        const coverData = buildResponsiveImageData(coverImage);

        if (coverData) {
            result.push({
                alt: coverData.alt,
                focalX: coverData.focalX,
                focalY: coverData.focalY,
                id: 'cover',
                srcSet: coverData.srcSet,
                url: coverData.src
            });
        }

        if (walk.gallery) {
            for (const entry of walk.gallery) {
                const img = typeof entry.image === 'object' ? entry.image : null;
                const imgData = buildResponsiveImageData(img);

                if (imgData && entry.id) {
                    result.push({
                        alt: imgData.alt,
                        focalX: imgData.focalX,
                        focalY: imgData.focalY,
                        id: entry.id,
                        srcSet: imgData.srcSet,
                        url: imgData.src
                    });
                }
            }
        }

        return result;
    }, [coverImage, walk.gallery]);

    /** CTA button label. */
    const ctaLabel = useMemo(() => {
        if (inLibrary) return t('walkDetail.open');
        if (isFree) return adding ? t('walkDetail.adding') : t('walkDetail.addToLibrary');

        return `${t('walkDetail.buy')} — ${formatWalkPrice(walk, false)}`;
    }, [inLibrary, isFree, adding, t, walk]);

    const ogImage = useMemo(() => resolveImageUrl(coverImage), [coverImage]);

    const cityGeo = useMemo(() => {
        if (!city?.location) return null;

        // city.location is GeoJSON [lng, lat]
        return {
            lat: city.location[1],
            lng: city.location[0],
            placename: city.name,
            region: 'DE'
        };
    }, [city]);

    const markers = useMemo(() => toPreviewMarkers(walk), [walk]);

    const jsonLd = useMemo(() => [
        buildWalkJsonLd(walk, [
            {
                name: 'Home',
                url: `${process.env.NEXT_PUBLIC_BASE_URL}/`
            },
            {
                name: t('walks.title'),
                url: `${process.env.NEXT_PUBLIC_BASE_URL}/walks`
            },
            {
                name: walk.title,
                url: `${process.env.NEXT_PUBLIC_BASE_URL}/walks/${walk.slug}`
            }
        ], reviews)
    ], [walk, reviews, t]);

    const handleAddToLibrary = useCallback(async () => {
        if (!isAuthenticated) {
            await router.push(`/login?redirect=/walks/${walk.slug}`);

            return;
        }

        if (inLibrary) {
            await router.push(`/walks/${walk.slug}/start`);

            return;
        }

        if (isFree) {
            setAdding(true);

            try {
                await addToLibrary(walk.id);
            } finally {
                setAdding(false);
            }

            return;
        }

        // Kostenpflichtig: zur Checkout-Seite weiterleiten
        await router.push(`/walks/${walk.slug}/checkout`);
    }, [isAuthenticated, isFree, inLibrary, router, walk.slug, walk.id, addToLibrary]);

    return {
        adding,
        authLoading,
        categories: (walk.categories ?? []).filter(c => typeof c === 'object'),
        categoryBadges: (walk.categories ?? [])
            .filter(c => typeof c === 'object')
            .map(cat => ({
                icon: cat.predefinedIcon ? createElement(CATEGORY_ICON_MAP[cat.predefinedIcon]) : null,
                label: cat.name
            })),
        checkoutStatus,
        city,
        cityGeo,
        coverImage,
        ctaLabel,
        durationLabel,
        guide,
        guideProfileImage,
        handleAddToLibrary,
        hasMapData: walk.waypoints?.docs?.some(wp => typeof wp === 'object' && wp.location) ?? false,
        heroImages,
        heroImageUrl,
        inLibrary,
        isFree,
        jsonLd,
        markers,
        ogImage,
        priceLabel,
        statTiles,
        t,
        terrainLabels: (walk.general?.terrain ?? []).map(v => t(TERRAIN_KEYS[v] ?? v)),
        waypointPreviews
    };
};